'use client'

import { useState, useEffect, useRef, useCallback } from 'react'
import { ChatMessage } from './message'
import {
  PromptInput,
  PromptInputTextarea,
  PromptInputSubmit
} from './prompt-input'
import { ChatList } from './chat-list'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Check, X, ThumbsUp, ChevronLeft, Square, Loader2, ArrowDown, Sparkles, Search, Zap, Terminal, MoreVertical, Copy, Trash2, FolderOpen } from 'lucide-react'
import type { ChatMeta } from '@/lib/chat-storage'
import type { RunMeta } from '@/lib/run-storage'
import { getProjects } from '@/app/actions/projects'

// Extended chat type with run info
type ChatWithRun = ChatMeta & {
  run?: RunMeta;
  // Legacy fields for compatibility - derived from run
  status?: 'idle' | 'running' | 'stopped' | 'error' | 'idle_timeout';
  usage?: { inputTokens: number; outputTokens: number; totalCost: number };
  matchType?: 'title' | 'content';
}

// Map backend run status to frontend chat status
// Backend: 'starting' | 'running' | 'completed' | 'error' | 'crashed' | 'oom_killed' | 'stopped' | 'idle_timeout'
// Frontend: 'idle' | 'running' | 'stopped' | 'error' | 'idle_timeout'
function mapRunStatus(status: string | undefined): ChatWithRun['status'] | undefined {
  if (!status) return undefined;
  switch (status) {
    case 'starting':
    case 'running':
      return 'running';
    case 'completed':
    case 'stopped':
      return 'stopped';
    case 'crashed':
    case 'oom_killed':
    case 'error':
      return 'error';
    case 'idle_timeout':
      return 'idle_timeout';
    default:
      return undefined;
  }
}

import { DEFAULT_CHAT_CONFIG, AVAILABLE_MODELS, THINKING_LEVELS, AVAILABLE_AGENTS, type ChatConfig, type ProjectAccess } from '@/lib/chat-config'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

import type { HistoryEntry } from './message'
import { useSSEEvent } from '@/components/providers/sse-provider'

const MODEL_ICONS = {
  'gemini-3-flash-preview': Zap,
  'gemini-3-pro-preview': Sparkles,
} as const;

const AGENT_ICONS = {
  'main': Terminal,
  'research': Search,
} as const;

interface ConnectionError {
  message: string
  details?: string
}

interface ChatProps {
  initialChats: ChatWithRun[]
  initialHasMore?: boolean
  initialNextCursor?: string | null
}

const QUICK_REPLIES = [
  { label: 'Yes, proceed', icon: Check, value: 'Yes, proceed', variant: 'default' as const },
  { label: 'No', icon: X, value: 'No, cancel that', variant: 'outline' as const },
  { label: 'Show me more options', icon: ThumbsUp, value: 'Show me more options', variant: 'outline' as const },
]

// Context window sizes for models
const MODEL_CONTEXT_WINDOWS: Record<string, number> = {
  'gemini-3-flash-preview': 1_000_000,
  'gemini-3-pro-preview': 1_000_000,
};

// Short model names for display
const MODEL_SHORT_NAMES: Record<string, string> = {
  'gemini-3-flash-preview': 'Flash',
  'gemini-3-pro-preview': 'Pro',
};

// Short thinking level names
const THINKING_SHORT_NAMES: Record<string, string> = {
  'none': '—',
  'low': 'L',
  'medium': 'M',
  'high': 'H',
};

// Format token count for display (e.g., 1200 -> 1.2K, 1500000 -> 1.5M)
function formatTokens(count: number): string {
  if (count >= 1_000_000) {
    return `${(count / 1_000_000).toFixed(1)}M`;
  }
  if (count >= 1_000) {
    return `${(count / 1_000).toFixed(1)}K`;
  }
  return count.toString();
}

// Format cost for display
function formatCost(cost: number): string {
  if (cost >= 1) {
    return `$${cost.toFixed(2)}`;
  }
  if (cost >= 0.01) {
    return `$${cost.toFixed(2)}`;
  }
  return `$${cost.toFixed(4)}`;
}

// URL parameter helpers for chat persistence
function getChatFromUrl(): string | null {
  if (typeof window === 'undefined') return null
  const params = new URLSearchParams(window.location.search)
  return params.get('chat')
}

function updateChatInUrl(chatId: string | null) {
  const url = new URL(window.location.href)
  if (chatId) {
    url.searchParams.set('chat', chatId)
  } else {
    url.searchParams.delete('chat')
  }
  window.history.replaceState({}, '', url.toString())
}

export function Chat({ initialChats, initialHasMore = false, initialNextCursor = null }: ChatProps) {
  const [chats, setChats] = useState<ChatWithRun[]>(() =>
    initialChats.map(chat => ({
      ...chat,
      status: mapRunStatus(chat.status as string) || chat.status
    }))
  )
  const [selectedChatId, setSelectedChatId] = useState<string | null>(null)
  const [isPendingChat, setIsPendingChat] = useState(false)
  const [pendingConfig, setPendingConfig] = useState<ChatConfig>(DEFAULT_CHAT_CONFIG)
  const [history, setHistory] = useState<HistoryEntry[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isConnected, setIsConnected] = useState(false)
  const [connectionError, setConnectionError] = useState<ConnectionError | null>(null)
  const [isCreatingChat, setIsCreatingChat] = useState(false)
  const [deletingChatId, setDeletingChatId] = useState<string | null>(null)
  const [stoppingChatId, setStoppingChatId] = useState<string | null>(null)

  // Project selection state
  const [availableProjects, setAvailableProjects] = useState<{ name: string }[]>([])
  const [selectedProjects, setSelectedProjects] = useState<ProjectAccess[]>([])
  const [isLoadingProjects, setIsLoadingProjects] = useState(false)
  const [isProjectDialogOpen, setIsProjectDialogOpen] = useState(false)

  // Pagination state
  const [hasMore, setHasMore] = useState(initialHasMore)
  const [nextCursor, setNextCursor] = useState<string | null>(initialNextCursor)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const scrollRef = useRef<HTMLDivElement>(null)
  const eventSourceRef = useRef<EventSource | null>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  // Track hydration to avoid React error #418 (hydration mismatch)
  const isHydratedRef = useRef(false)

  const [input, setInput] = useState('')
  // Use ref for autoScroll to avoid re-renders on scroll state changes
  const autoScrollRef = useRef(true)
  const [showScrollButton, setShowScrollButton] = useState(false)
  const isProgrammaticScrollRef = useRef(false)
  const [chatToDelete, setChatToDelete] = useState<string | null>(null)

  // Subscribe to global SSE for chat_update events (updates from any chat)
  // This handles status updates when user is viewing one chat but another changes
  useSSEEvent('chat_update', (event) => {
    const chatId = event.chatId
    const updates = event.chat as Partial<ChatWithRun> | undefined
    if (chatId && updates) {
      setChats(prev => prev.map(c => {
        if (c?.id !== chatId) return c
        // Deduplication: skip if title/status already match
        const newStatus = mapRunStatus((updates as Record<string, unknown>).status as string)
        const isTitleSame = !updates.title || c.title === updates.title
        const isStatusSame = !newStatus || c.status === newStatus
        if (isTitleSame && isStatusSame) {
          return c
        }
        return {
          ...c,
          ...updates,
          status: newStatus || c.status
        }
      }).filter(Boolean) as ChatWithRun[])
    }
  }, [])

  // Mark as hydrated after first render
  useEffect(() => {
    isHydratedRef.current = true
  }, [])

  // Sync chats from server on mount and when props change
  // Handles: Next.js navigation, revalidation
  const initialChatsRef = useRef(initialChats)
  useEffect(() => {
    if (initialChatsRef.current !== initialChats) {
      console.log('[Chat] Server chats prop changed, syncing', {
        prev: initialChatsRef.current.length,
        new: initialChats.length
      })
      setChats(initialChats.map(chat => ({
        ...chat,
        status: mapRunStatus(chat.status as string) || chat.status
      })))
      initialChatsRef.current = initialChats
    }
  }, [initialChats])

  // Handle bfcache restore - fetch fresh data when page is restored
  useEffect(() => {
    const handlePageShow = async (event: PageTransitionEvent) => {
      if (event.persisted) {
        // Page was restored from bfcache - fetch fresh chat list
        console.log('[Chat] Page restored from bfcache, fetching fresh chats')
        try {
          const { listAgentChatsPaginated } = await import('@/app/actions/agent')
          const result = await listAgentChatsPaginated({ limit: 20 })
          console.log('[Chat] Fetched fresh chats:', result.chats.length)
          setChats(result.chats.map(chat => ({
            ...chat,
            status: mapRunStatus(chat.run?.status),
            usage: chat.run?.usage,
          })))
          // Reset pagination state
          setHasMore(result.hasMore)
          setNextCursor(result.nextCursor)
          setSearchQuery('')
        } catch (error) {
          console.error('[Chat] Failed to fetch fresh chats:', error)
        }
      }
    }
    window.addEventListener('pageshow', handlePageShow)
    return () => window.removeEventListener('pageshow', handlePageShow)
  }, [])

  // Initialize selectedChatId from URL on mount
  useEffect(() => {
    const chatId = getChatFromUrl()
    if (chatId && initialChats.some(c => c.id === chatId)) {
      setSelectedChatId(chatId)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Only run on mount

  // Sync URL with selected chat
  useEffect(() => {
    if (!isHydratedRef.current) return
    updateChatInUrl(selectedChatId)
  }, [selectedChatId])

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      const chatId = getChatFromUrl()
      if (chatId && chats.some(c => c.id === chatId)) {
        setSelectedChatId(chatId)
      } else {
        setSelectedChatId(null)
      }
    }
    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [chats])

  // Auto scroll to bottom
  const scrollToBottom = useCallback((force = false, smooth = true) => {
    if (scrollRef.current && (autoScrollRef.current || force)) {
      // Only mark as programmatic scroll for smooth animations (button click)
      // This prevents useEffect from overriding smooth scroll with instant
      if (smooth) {
        isProgrammaticScrollRef.current = true
        setTimeout(() => {
          isProgrammaticScrollRef.current = false
        }, 350)
      }

      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: smooth ? 'smooth' : 'instant'
      })

      if (force) {
        autoScrollRef.current = true
        setShowScrollButton(false)
      }
    }
  }, [])

  // Handle scroll events to detect user scrolling up
  // Throttled to avoid excessive processing
  const scrollTickingRef = useRef(false)
  const handleScroll = useCallback(() => {
    if (scrollTickingRef.current) return
    scrollTickingRef.current = true

    requestAnimationFrame(() => {
      scrollTickingRef.current = false
      if (!scrollRef.current) return

      // Ignore scroll events during programmatic scroll animation
      if (isProgrammaticScrollRef.current) return

      const { scrollTop, scrollHeight, clientHeight } = scrollRef.current
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight
      const isNearBottom = distanceFromBottom < 100 // 100px threshold

      if (isNearBottom) {
        // User scrolled back to bottom
        autoScrollRef.current = true
        setShowScrollButton(prev => prev ? false : prev)
      } else {
        // User scrolled up
        autoScrollRef.current = false
        setShowScrollButton(prev => !prev ? true : prev)
      }
    })
  }, [])

  // Track previous history length to detect new messages
  const prevHistoryLengthRef = useRef(history.length)

  useEffect(() => {
    // Only scroll when new messages are added
    const hasNewMessages = history.length > prevHistoryLengthRef.current
    prevHistoryLengthRef.current = history.length

    if (autoScrollRef.current && hasNewMessages) {
      scrollToBottom()
    }
  }, [history, scrollToBottom])

  // Reset auto-scroll when changing chats
  useEffect(() => {
    autoScrollRef.current = true
    setShowScrollButton(false)
  }, [selectedChatId])

  // Listen for async content render events (Mermaid diagrams)
  // Scrolls to bottom when diagrams finish rendering
  useEffect(() => {
    const handleMermaidRendered = () => {
      if (autoScrollRef.current) {
        // Use setTimeout to ensure DOM has updated after React state change
        setTimeout(() => {
          scrollToBottom(false, false) // instant scroll, not smooth
        }, 50)
      }
    }

    window.addEventListener('mermaid-rendered', handleMermaidRendered)
    return () => window.removeEventListener('mermaid-rendered', handleMermaidRendered)
  }, [scrollToBottom])

  // Connect to SSE for history updates
  useEffect(() => {
    if (!selectedChatId) {
      setHistory([])
      setConnectionError(null)
      return
    }

    setIsLoading(true)
    setConnectionError(null)

    // Connect to Next.js SSE proxy with chatId (handles auth via session cookie)
    const sseUrl = `/api/events?chatId=${selectedChatId}`
    console.log(`[Chat] SSE connecting to proxy`)
    const eventSource = new EventSource(sseUrl)
    eventSourceRef.current = eventSource
    let retryCount = 0
    const maxRetries = 3

    eventSource.onopen = () => {
      console.log(`[Chat] SSE connected for ${selectedChatId}`)
      setIsConnected(true)
      // Don't set isLoading to false here - wait for history to arrive
      setConnectionError(null)
      retryCount = 0
    }

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data)

        if (data.type === 'history') {
          console.log(`[Chat] SSE history: ${data.entries?.length} entries`)
          setHistory(data.entries)
          setIsLoading(false)
          // Delayed scrolls to catch async content (Mermaid diagrams, images)
          if (autoScrollRef.current) {
            setTimeout(() => scrollToBottom(false, false), 100)
            setTimeout(() => scrollToBottom(false, false), 500)
            setTimeout(() => scrollToBottom(false, false), 1000)
          }
        } else if (data.type === 'entry') {
          console.log(`[Chat] SSE entry: ${data.entry?.type}`, data.entry)
          setHistory(prev => {
            const entry = data.entry

            // Handle streaming chunks
            if (entry.type === 'chunk') {
              // Find last meaningful entry (skip meta updates)
              let lastIndex = prev.length - 1
              while (lastIndex >= 0 && (prev[lastIndex].type === 'meta_update' || prev[lastIndex].type === 'sub_agent_result')) {
                lastIndex--
              }
              const last = lastIndex >= 0 ? prev[lastIndex] : null

              if (last && last.type === 'assistant') {
                // Append to last assistant message
                const newHistory = [...prev]
                newHistory[lastIndex] = { ...last, content: (last.content || '') + (entry.content || '') }
                return newHistory
              } else {
                // Create new assistant message from chunk
                return [...prev, { ...entry, type: 'assistant' }]
              }
            }

            // Handle streaming thinking
            if (entry.type === 'thinking') {
              const last = prev[prev.length - 1]
              if (last && last.type === 'thinking') {
                // Append to last thinking message
                return [...prev.slice(0, -1), { ...last, content: (last.content || '') + (entry.content || '') }]
              } else {
                // Create new thinking message
                return [...prev, entry]
              }
            }

            // Handle final assistant message (replace potential chunk-based message)
            if (entry.type === 'assistant') {
              // Find last meaningful entry (skip meta updates)
              let lastIndex = prev.length - 1
              while (lastIndex >= 0 && (prev[lastIndex].type === 'meta_update' || prev[lastIndex].type === 'sub_agent_result')) {
                lastIndex--
              }
              const last = lastIndex >= 0 ? prev[lastIndex] : null

              if (last && last.type === 'assistant') {
                const newHistory = [...prev]
                newHistory[lastIndex] = entry
                return newHistory
              }
            }

            // Handle done event - just add it to history (UI will show "Agent finished at {timestamp}")
            // No need to add extra messages, the done event itself is displayed

            // Prevent duplicates for non-streaming entries (checking against the last entry)
            if (entry.type !== 'chunk' && entry.type !== 'thinking' && prev.length > 0) {
              const last = prev[prev.length - 1]
              if (last.ts === entry.ts && last.content === entry.content && last.type === entry.type) {
                console.log('[Chat] Skipping duplicate entry:', entry)
                return prev
              }
            }

            const newHistory = [...prev, entry]
            console.log(`[Chat] History updated: ${newHistory.length} entries`)
            return newHistory
          })
        } else if (data.type === 'chat_update') {
          // SSE sends { type: 'chat_update', chatId: string, data: Partial<Chat> }
          // For chat-specific SSE, chatId may be missing - use selectedChatId
          const chatId = data.chatId || selectedChatId
          const updates = data.data as Partial<ChatWithRun> | undefined
          console.log(`[Chat] SSE chat_update for ${chatId}:`, updates)
          if (chatId && updates) {
            setChats(prev => prev.map(c => {
              if (c?.id !== chatId) return c
              // Deduplication: skip if title/status already match (prevents redundant updates after chat_state)
              const newStatus = mapRunStatus(updates.status as string)
              const isTitleSame = !updates.title || c.title === updates.title
              const isStatusSame = !newStatus || c.status === newStatus
              if (isTitleSame && isStatusSame && !updates.usage) {
                console.log(`[Chat] Skipping duplicate chat_update for ${chatId}`)
                return c
              }
              return {
                ...c,
                ...updates,
                status: newStatus || c.status
              }
            }).filter(Boolean) as ChatWithRun[])
          }
        } else if (data.type === 'chats_init') {
          // Initial chat list sync from SSE - replaces potentially stale initialChats
          // Only update after hydration to avoid React error #418
          // Only update if NOT searching (SSE sends full list, search is client-controlled)
          console.log(`[Chat] SSE chats_init: ${data.chats?.length} chats, hydrated: ${isHydratedRef.current}, searching: ${!!searchQuery}`)
          if (isHydratedRef.current && data.chats && Array.isArray(data.chats) && !searchQuery) {
            setChats(data.chats.map((chat: ChatWithRun) => ({
              ...chat,
              status: mapRunStatus(chat.status as string) || chat.status
            })))
            // Update pagination state if provided
            if (typeof data.hasMore === 'boolean') {
              setHasMore(data.hasMore)
            }
            if (data.nextCursor !== undefined) {
              setNextCursor(data.nextCursor)
            }
          }
        } else if (data.type === 'chat_state') {
          // Sync current chat state on connect (fixes SSE race condition)
          const chatState = data.chat as { id: string; title?: string; status?: string; usage?: ChatWithRun['usage'] } | undefined
          console.log(`[Chat] SSE chat_state:`, chatState)
          if (chatState?.id) {
            setChats(prev => prev.map(c => {
              if (c?.id !== chatState.id) return c
              return {
                ...c,
                ...(chatState.title ? { title: chatState.title } : {}),
                ...(chatState.status ? { status: mapRunStatus(chatState.status) } : {}),
                ...(chatState.usage ? { usage: chatState.usage } : {})
              }
            }).filter(Boolean) as ChatWithRun[])
          }
        } else if (data.type === 'error') {
          console.error(`[Chat] SSE error:`, data)
          setConnectionError({
            message: data.message || 'Unknown error',
            details: data.details
          })
        } else if (data.type !== 'ping' && data.type !== 'connected') {
          console.log(`[Chat] SSE message:`, data.type)
        }
      } catch (err) {
        console.error('[Chat] SSE parse error:', err)
      }
    }

    eventSource.onerror = () => {
      retryCount++
      // Only warn on first few retries, error on max
      if (retryCount < maxRetries) {
        console.warn(`[Chat] SSE connection issue for ${selectedChatId}, retry ${retryCount}/${maxRetries}`)
      } else {
        console.error(`[Chat] SSE connection failed for ${selectedChatId} after ${retryCount} retries`)
      }

      setIsConnected(false)

      if (retryCount >= maxRetries) {
        setIsLoading(false)
        setConnectionError({
          message: 'Connection failed',
          details: `SSE connection closed after ${retryCount} retries. Try refreshing the page.`
        })
        eventSource.close()
      }
    }

    return () => {
      console.log(`[Chat] SSE closing for ${selectedChatId}`)
      eventSource.close()
      eventSourceRef.current = null
    }
  }, [selectedChatId])

  // Load more chats (pagination)
  const handleLoadMore = useCallback(async () => {
    if (isLoadingMore || !hasMore || !nextCursor) return

    setIsLoadingMore(true)
    try {
      const { listAgentChatsPaginated } = await import('@/app/actions/agent')
      const result = await listAgentChatsPaginated({
        cursor: nextCursor,
        limit: 20,
        search: searchQuery || undefined,
      })

      const newChats = result.chats.map(chat => ({
        ...chat,
        status: mapRunStatus(chat.run?.status) as ChatWithRun['status'],
        usage: chat.run?.usage,
        matchType: chat.matchType,
      }))

      // Deduplicate by ID
      setChats(prev => {
        const existingIds = new Set(prev.map(c => c.id))
        const unique = newChats.filter(c => !existingIds.has(c.id))
        return [...prev, ...unique]
      })
      setNextCursor(result.nextCursor)
      setHasMore(result.hasMore)
    } catch (error) {
      console.error('[Chat] Failed to load more chats:', error)
    } finally {
      setIsLoadingMore(false)
    }
  }, [isLoadingMore, hasMore, nextCursor, searchQuery])

  // Search chats
  const handleSearch = useCallback(async (query: string) => {
    setSearchQuery(query)
    setIsLoadingMore(true)

    try {
      const { listAgentChatsPaginated } = await import('@/app/actions/agent')
      const result = await listAgentChatsPaginated({
        limit: 20,
        search: query || undefined,
      })

      const newChats = result.chats.map(chat => ({
        ...chat,
        status: mapRunStatus(chat.run?.status) as ChatWithRun['status'],
        usage: chat.run?.usage,
        matchType: chat.matchType,
      }))

      setChats(newChats)
      setNextCursor(result.nextCursor)
      setHasMore(result.hasMore)
    } catch (error) {
      console.error('[Chat] Failed to search chats:', error)
    } finally {
      setIsLoadingMore(false)
    }
  }, [])

  // Show pending chat UI with settings
  const handleNewChat = async () => {
    setSelectedChatId(null)
    setHistory([])
    setIsPendingChat(true)
    setPendingConfig(DEFAULT_CHAT_CONFIG)
    setSelectedProjects([])
    setConnectionError(null)

    // Fetch available projects
    setIsLoadingProjects(true)
    try {
      const projects = await getProjects()
      setAvailableProjects(projects.map((p: { name: string }) => ({ name: p.name })))
    } catch (error) {
      console.error('[Chat] Failed to load projects:', error)
      setAvailableProjects([])
    } finally {
      setIsLoadingProjects(false)
    }
  }

  // Actually create chat with config and first message
  const handleCreateChatWithMessage = async (message: string) => {
    if (isCreatingChat || !message.trim()) return

    // Merge selected projects into config
    const configWithProjects: ChatConfig = {
      ...pendingConfig,
      projects: selectedProjects.length > 0 ? selectedProjects : undefined,
    }

    console.log('[Chat] Creating new chat with message...', {
      config: configWithProjects,
      messageLength: message.trim().length
    })
    setIsCreatingChat(true)
    setConnectionError(null)

    try {
      console.log('[Chat] Importing startAgentChat...')
      const { startAgentChat } = await import('@/app/actions/agent')
      console.log('[Chat] Calling startAgentChat...')
      const result = await startAgentChat(configWithProjects, message.trim())
      console.log('[Chat] startAgentChat result:', result)

      if (!result.success) {
        console.error('[Chat] Failed to start chat:', result.error)
        setConnectionError({
          message: 'Failed to create chat',
          details: result.error
        })

        // If chat was created but containers failed, still add it to the list
        if (result.chat) {
          console.log('[Chat] Adding failed chat to list:', result.chat.id)
          const chatWithRun: ChatWithRun = {
            ...result.chat,
            status: 'error',
          }
          setChats(prev => [chatWithRun, ...prev])
          setSelectedChatId(result.chat.id)
          setIsPendingChat(false)
        }
        return
      }

      const { chat, run } = result.data
      console.log('[Chat] New chat created:', chat.id, 'run:', run.id)
      const chatWithRun: ChatWithRun = {
        ...chat,
        run,
        status: mapRunStatus(run.status),
        usage: run.usage,
      }
      setChats(prev => {
        const updated = [chatWithRun, ...prev]
        console.log('[Chat] Updated chat list, count:', updated.length)
        return updated
      })
      setSelectedChatId(chat.id)
      setIsPendingChat(false)
      setInput('')
    } catch (error) {
      console.error('[Chat] Failed to start chat:', error)
      setConnectionError({
        message: 'Failed to create chat',
        details: error instanceof Error ? error.message : String(error)
      })
    } finally {
      setIsCreatingChat(false)
    }
  }

  const handleSelectChat = (chatId: string) => {
    // Don't re-select already selected chat (prevents infinite loader)
    if (chatId === selectedChatId) return

    // Clear history immediately to avoid showing old messages
    setHistory([])
    setIsLoading(true)
    setSelectedChatId(chatId)
    setIsPendingChat(false)
  }

  const handleDeleteChat = async (chatId: string) => {
    if (deletingChatId) return

    setDeletingChatId(chatId)
    try {
      const { deleteAgentChat } = await import('@/app/actions/agent')
      const result = await deleteAgentChat(chatId)

      if (!result.success) {
        console.error('[Chat] Failed to delete chat:', result.error)
        setConnectionError({
          message: 'Failed to delete chat',
          details: result.error
        })
        return
      }

      setChats(prev => prev.filter(c => c.id !== chatId))
      if (selectedChatId === chatId) {
        setSelectedChatId(null)
      }
    } catch (error) {
      console.error('[Chat] Failed to delete chat:', error)
      setConnectionError({
        message: 'Failed to delete chat',
        details: error instanceof Error ? error.message : String(error)
      })
    } finally {
      setDeletingChatId(null)
      setChatToDelete(null)
    }
  }

  const handleCopyId = async (chatId: string) => {
    try {
      await navigator.clipboard.writeText(chatId)
    } catch (error) {
      console.error('[Chat] Failed to copy ID:', error)
    }
  }

  const handleStopChat = async (chatId: string) => {
    if (stoppingChatId) return

    // Find the run ID for this chat
    const chat = chats.find(c => c.id === chatId)
    const runId = chat?.agent_run_id || chat?.run?.id
    if (!runId) {
      console.error('[Chat] No run ID for chat:', chatId)
      return
    }

    setStoppingChatId(chatId)
    try {
      const { stopAgentRun } = await import('@/app/actions/agent')
      const result = await stopAgentRun(runId)

      if (!result.success) {
        console.error('[Chat] Failed to stop run:', result.error)
        setConnectionError({
          message: 'Failed to stop chat',
          details: result.error
        })
        return
      }

      setChats(prev => prev.map(c =>
        c?.id === chatId ? { ...c, status: 'stopped' as const } : c
      ).filter(Boolean) as ChatWithRun[])
    } catch (error) {
      console.error('[Chat] Failed to stop chat:', error)
      setConnectionError({
        message: 'Failed to stop chat',
        details: error instanceof Error ? error.message : String(error)
      })
    } finally {
      setStoppingChatId(null)
    }
  }

  const [isSending, setIsSending] = useState(false)

  const handleSendMessage = async (message: string) => {
    if (!selectedChatId || isSending) return

    // Find the run ID for this chat
    const chat = chats.find(c => c.id === selectedChatId)
    const runId = chat?.agent_run_id || chat?.run?.id
    if (!runId) {
      console.error('[Chat] No run ID for chat:', selectedChatId)
      return
    }

    // If chat is stopped/completed, restart it first
    let currentRunId = runId
    if (chat.status === 'stopped' || chat.status === 'error') {
      console.log('[Chat] Auto-restarting chat before sending message...')
      try {
        const { restartAgentRun } = await import('@/app/actions/agent')
        const restartResult = await restartAgentRun(runId)

        if (!restartResult.success) {
          throw new Error(restartResult.error || 'Failed to auto-restart chat')
        }

        // Update local state with new run
        const { run: newRun } = restartResult.data
        currentRunId = newRun.id

        // Update the chat object in the list immediately to reflect new status
        setChats(prev => prev.map(c =>
          c?.id === selectedChatId ? {
            ...c,
            agent_run_id: newRun.id,
            run: newRun,
            status: mapRunStatus(newRun.status),
            usage: newRun.usage,
          } : c
        ).filter(Boolean) as ChatWithRun[])
      } catch (error) {
        console.error('[Chat] Failed to auto-restart:', error)
        setConnectionError({
          message: 'Failed to restart chat',
          details: error instanceof Error ? error.message : String(error)
        })
        setIsSending(false)
        return
      }
    }

    setIsSending(true)
    // Don't clear input here, clear it after success or let caller handle it
    // But caller needs to know when to clear.
    // For now we clear immediately for better UX
    setInput('')

    try {
      const { sendAgentMessage, getChatWithRun } = await import('@/app/actions/agent')
      // Note: sendAgentMessage expects chatId, not runId
      const result = await sendAgentMessage(selectedChatId, message)

      // If agent was auto-restarted, update chat state with new run
      if (result.restarted && result.runId !== currentRunId) {
        console.log(`[Chat] Agent restarted: ${currentRunId} -> ${result.runId}`)

        // Get updated chat with new run metadata
        const chatData = await getChatWithRun(selectedChatId)
        if (chatData && chatData.run) {
          // Update chat with new run
          setChats(prev => prev.map(c =>
            c?.id === selectedChatId ? {
              ...c,
              agent_run_id: result.runId,
              run: chatData.run!,
              status: mapRunStatus(chatData.run!.status),
              usage: chatData.run!.usage,
            } : c
          ).filter(Boolean) as ChatWithRun[])
        }
      }
    } catch (error) {
      console.error('Failed to send message:', error)
      setConnectionError({
        message: 'Failed to send message',
        details: error instanceof Error ? error.message : String(error)
      })
    } finally {
      setIsSending(false)
    }
  }

  const handleSubmit = () => {
    const trimmed = input.trim()
    if (!trimmed || isSending || isCreatingChat) return

    // If pending chat or no chat selected (new chat UI after refresh), create chat with first message
    if (isPendingChat || !selectedChatId) {
      handleCreateChatWithMessage(trimmed)
    } else {
      handleSendMessage(trimmed)
    }
  }

  const handleQuickReply = (value: string) => {
    if (isSending) return
    handleSendMessage(value)
  }

  // Handle confirmation responses for dangerous operations (vds.* tools)
  const handleConfirmation = async (confirmationId: string, approved: boolean) => {
    if (!selectedChatId) return

    // Find the run ID for this chat
    const chat = chats.find(c => c.id === selectedChatId)
    const runId = chat?.agent_run_id || chat?.run?.id
    if (!runId) {
      console.error('[Chat] No run ID for chat:', selectedChatId)
      return
    }

    try {
      const { sendAgentConfirmation } = await import('@/app/actions/agent')
      await sendAgentConfirmation(runId, confirmationId, approved)
    } catch (error) {
      console.error('Failed to send confirmation:', error)
    }
  }

  // Find pending confirmation request (last confirmation_request without a matching confirmation_response)
  const pendingConfirmationId = (() => {
    for (let i = history.length - 1; i >= 0; i--) {
      const entry = history[i]
      if (entry.type === 'confirmation_response') {
        // Found a response, check if it matches a request
        return null
      }
      if (entry.type === 'confirmation_request' && entry.confirmationId) {
        // Check if there's a response after this request
        const hasResponse = history.slice(i + 1).some(
          e => e.type === 'confirmation_response' && e.confirmationId === entry.confirmationId
        )
        if (!hasResponse) {
          return entry.confirmationId
        }
      }
    }
    return null
  })()

  // Check if agent is processing
  // Show spinner when: sending, last is user message, or last is tool_result (waiting for assistant response)
  // Also show when history is empty but chat is running (waiting for first response)
  const lastEntry = history[history.length - 1]
  const selectedChat = chats.find(c => c.id === selectedChatId)
  const isAgentProcessing = isSending ||
    // Empty history but chat is running - waiting for first response
    (history.length === 0 && selectedChat?.status === 'running') ||
    // Has history and waiting for response
    (history.length > 0 && (
      lastEntry?.type === 'user' ||
      lastEntry?.type === 'tool_result' ||
      lastEntry?.type === 'tool_call'
    ))

  // Check if the last assistant message is asking a question (show quick replies)
  const lastAssistantMessage = [...history].reverse().find(e => e.type === 'assistant')
  const isAskingQuestion = lastAssistantMessage?.content && (
    lastAssistantMessage.content.includes('?') ||
    lastAssistantMessage.content.toLowerCase().includes('would you like') ||
    lastAssistantMessage.content.toLowerCase().includes('do you want') ||
    lastAssistantMessage.content.toLowerCase().includes('shall i') ||
    lastAssistantMessage.content.toLowerCase().includes('should i')
  )

  return (
    <div className="flex h-full">
      {/* Chat list sidebar */}
      <ChatList
        chats={chats}
        selectedChatId={selectedChatId}
        onNewChat={handleNewChat}
        onSelectChat={handleSelectChat}
        onDeleteChat={handleDeleteChat}
        onStopChat={handleStopChat}
        onLoadMore={handleLoadMore}
        onSearch={handleSearch}
        isCreatingChat={isCreatingChat}
        deletingChatId={deletingChatId}
        stoppingChatId={stoppingChatId}
        isLoadingMore={isLoadingMore}
        hasMore={hasMore}
        searchQuery={searchQuery}
        className={cn(
          "w-64 shrink-0",
          (selectedChatId || isPendingChat) ? "hidden md:flex" : "flex w-full md:w-64"
        )}
      />

      {/* Main chat area */}
      <div className={cn(
        "flex-1 flex flex-col min-w-0 bg-card",
        !selectedChatId && !isPendingChat && "hidden md:flex"
      )}>
        {(isPendingChat || !selectedChat) ? (
          // New chat setup UI
          <div className="flex-1 flex flex-col">
            {/* Header */}
            <div className="p-3 sm:p-4 border-b sticky top-0 z-10 bg-card flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                className="md:hidden h-8 w-8 shrink-0"
                onClick={() => setIsPendingChat(false)}
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <h2 className="font-semibold">New Chat</h2>
            </div>

            {/* Settings */}
            <div className="flex-1 flex flex-col items-center justify-center p-6 gap-8">
              <div className="w-full max-w-md space-y-6">
                {/* Agent Selection */}
                <div className="space-y-3">
                  <Label className="text-sm font-medium">Agent</Label>
                  <div className="grid grid-cols-2 gap-3">
                    {AVAILABLE_AGENTS.map((agent) => {
                      const Icon = AGENT_ICONS[agent.id]
                      return (
                        <button
                          key={agent.id}
                          onClick={() => setPendingConfig(prev => ({ ...prev, agent: agent.id }))}
                          className={cn(
                            "flex flex-col items-start gap-1 p-3 rounded-lg border-2 transition-all text-left",
                            pendingConfig.agent === agent.id
                              ? "border-primary bg-primary/5"
                              : "border-muted hover:border-muted-foreground/30"
                          )}
                        >
                          <div className="flex items-center gap-2">
                            <Icon className="h-4 w-4" />
                            <span className="font-medium text-sm">{agent.name}</span>
                          </div>
                          <span className="text-xs text-muted-foreground">{agent.description}</span>
                        </button>
                      )
                    })}
                  </div>
                </div>

                {/* Model Selection - only for main agent */}
                {pendingConfig.agent === 'main' && (
                  <div className="space-y-3">
                    <Label className="text-sm font-medium">Model</Label>
                    <div className="grid grid-cols-2 gap-3">
                      {AVAILABLE_MODELS.map((model) => {
                        const Icon = MODEL_ICONS[model.id]
                        return (
                          <button
                            key={model.id}
                            onClick={() => setPendingConfig(prev => ({ ...prev, model: model.id }))}
                            className={cn(
                              "flex flex-col items-start gap-1 p-3 rounded-lg border-2 transition-all text-left",
                              pendingConfig.model === model.id
                                ? "border-primary bg-primary/5"
                                : "border-muted hover:border-muted-foreground/30"
                            )}
                          >
                            <div className="flex items-center gap-2">
                              <Icon className="h-4 w-4" />
                              <span className="font-medium text-sm">{model.name}</span>
                            </div>
                            <span className="text-xs text-muted-foreground">{model.description}</span>
                          </button>
                        )
                      })}
                    </div>
                  </div>
                )}

                {/* Thinking Level - only for main agent */}
                {pendingConfig.agent === 'main' && (
                  <div className="space-y-3">
                    <Label className="text-sm font-medium">Reasoning Depth</Label>
                    <div className="flex gap-2">
                      {THINKING_LEVELS.map((level) => (
                        <button
                          key={level.id}
                          onClick={() => setPendingConfig(prev => ({ ...prev, thinkingLevel: level.id }))}
                          className={cn(
                            "flex-1 py-2 px-3 rounded-lg border-2 transition-all text-sm",
                            pendingConfig.thinkingLevel === level.id
                              ? "border-primary bg-primary/5 font-medium"
                              : "border-muted hover:border-muted-foreground/30"
                          )}
                        >
                          {level.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Research agent info */}
                {pendingConfig.agent === 'research' && (
                  <div className="p-4 rounded-lg bg-muted/50 border border-muted">
                    <p className="text-sm text-muted-foreground">
                      Research agent uses <strong>Gemini 3 Pro</strong> with Google Search and URL analysis.
                      It will search the web, analyze relevant pages, and synthesize findings.
                    </p>
                  </div>
                )}

                {/* Project Selection - only for main agent */}
                {pendingConfig.agent === 'main' && (
                  <div className="space-y-3">
                    <Label className="text-sm font-medium">Project Access</Label>
                    <Button
                      variant="outline"
                      className="w-full justify-start gap-2"
                      onClick={() => setIsProjectDialogOpen(true)}
                      disabled={isLoadingProjects}
                    >
                      {isLoadingProjects ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <FolderOpen className="h-4 w-4" />
                      )}
                      <span className="flex-1 text-left">
                        {selectedProjects.length === 0
                          ? 'Select projects...'
                          : `${selectedProjects.length} project${selectedProjects.length !== 1 ? 's' : ''} selected`}
                      </span>
                    </Button>
                  </div>
                )}

                {/* Project Selection Dialog */}
                <Dialog open={isProjectDialogOpen} onOpenChange={setIsProjectDialogOpen}>
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle>Select Projects</DialogTitle>
                    </DialogHeader>
                    <div className="max-h-[300px] overflow-y-auto space-y-1 py-2">
                      {isLoadingProjects ? (
                        <div className="flex items-center justify-center py-8">
                          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                        </div>
                      ) : availableProjects.length === 0 ? (
                        <p className="text-sm text-muted-foreground py-4 text-center">No projects available</p>
                      ) : (
                        availableProjects.map((project) => {
                          const selected = selectedProjects.find(p => p.name === project.name)
                          return (
                            <div
                              key={project.name}
                              className="flex items-center gap-2 p-2 rounded hover:bg-muted/50"
                            >
                              <Checkbox
                                id={`project-dialog-${project.name}`}
                                checked={!!selected}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setSelectedProjects([...selectedProjects, { name: project.name, mode: 'ro' }])
                                  } else {
                                    setSelectedProjects(selectedProjects.filter(p => p.name !== project.name))
                                  }
                                }}
                              />
                              <label
                                htmlFor={`project-dialog-${project.name}`}
                                className="text-sm flex-1 cursor-pointer"
                              >
                                {project.name}
                              </label>
                              {selected && (
                                <Select
                                  value={selected.mode}
                                  onValueChange={(value: 'ro' | 'rw') => {
                                    setSelectedProjects(selectedProjects.map(p =>
                                      p.name === project.name ? { ...p, mode: value } : p
                                    ))
                                  }}
                                >
                                  <SelectTrigger className="w-24 h-7 text-xs">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="ro">read-only</SelectItem>
                                    <SelectItem value="rw">read-write</SelectItem>
                                  </SelectContent>
                                </Select>
                              )}
                            </div>
                          )
                        })
                      )}
                    </div>
                    <DialogFooter>
                      <Button onClick={() => setIsProjectDialogOpen(false)}>
                        Done
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </div>

            {/* Input */}
            <div className="py-2 sm:py-4 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
              <div className="px-3 sm:px-4 max-w-[800px] mx-auto w-full">
                <PromptInput
                  onSubmit={handleSubmit}
                  isLoading={isCreatingChat}
                >
                  <PromptInputTextarea
                    ref={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder={isCreatingChat ? 'Creating chat...' : 'Type your message to start...'}
                    disabled={isCreatingChat}
                  />
                  <PromptInputSubmit disabled={!input.trim() || isCreatingChat} />
                </PromptInput>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="p-3 sm:p-4 border-b sticky top-0 z-10 bg-card flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <Button
                  variant="ghost"
                  size="icon"
                  className="md:hidden h-8 w-8 shrink-0"
                  onClick={() => setSelectedChatId(null)}
                >
                  <ChevronLeft className="h-5 w-5" />
                </Button>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h2 className="font-semibold truncate">{selectedChat.title}</h2>
                    {selectedChat.config?.agent && (
                      <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-muted text-muted-foreground border">
                        {AVAILABLE_AGENTS.find(a => a.id === selectedChat.config?.agent)?.name || selectedChat.config.agent}
                      </span>
                    )}
                  </div>
                  <p className="text-xs sm:text-sm text-muted-foreground flex items-center gap-1.5">
                    {selectedChat.status === 'running' && (
                      <>
                        <span className="flex h-1.5 w-1.5 rounded-full bg-green-500" />
                        Running
                      </>
                    )}
                    {selectedChat.status === 'stopped' && (
                      <>
                        <span className="flex h-1.5 w-1.5 rounded-full bg-gray-500" />
                        Agent finished
                      </>
                    )}
                    {selectedChat.status === 'error' && (
                      <>
                        <span className="flex h-1.5 w-1.5 rounded-full bg-red-500" />
                        Error
                      </>
                    )}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {/* Model & Config display - desktop only */}
                {selectedChat.config && (
                  <div className="hidden sm:flex items-center gap-1.5 text-[10px] sm:text-xs text-muted-foreground font-mono">
                    <span
                      className="px-1.5 py-0.5 rounded bg-primary/10 text-primary font-medium"
                      title={`Model: ${selectedChat.config.model}`}
                    >
                      {MODEL_SHORT_NAMES[selectedChat.config.model] || selectedChat.config.model}
                    </span>
                    <span
                      className="px-1 py-0.5 rounded bg-violet-500/10 text-violet-600 dark:text-violet-400"
                      title={`Thinking: ${selectedChat.config.thinkingLevel}`}
                    >
                      🧠{THINKING_SHORT_NAMES[selectedChat.config.thinkingLevel] || selectedChat.config.thinkingLevel}
                    </span>
                  </div>
                )}

                {/* Context usage indicator - visible on all screens */}
                {selectedChat.usage && (selectedChat.usage.inputTokens > 0 || selectedChat.usage.outputTokens > 0) && selectedChat.config && (() => {
                  const contextWindow = MODEL_CONTEXT_WINDOWS[selectedChat.config.model] || 1_000_000;
                  const usedTokens = selectedChat.usage!.inputTokens + selectedChat.usage!.outputTokens;
                  const usagePercent = (usedTokens / contextWindow) * 100;
                  const remainingTokens = contextWindow - usedTokens;
                  const colorClass = usagePercent > 80 ? 'bg-red-500' : usagePercent > 50 ? 'bg-amber-500' : 'bg-green-500';
                  const textColorClass = usagePercent > 80 ? 'text-red-500' : usagePercent > 50 ? 'text-amber-500' : 'text-green-500';

                  return (
                    <Dialog>
                      <DialogTrigger asChild>
                        <button className="flex items-center gap-2 px-2 py-1 rounded hover:bg-muted transition-colors">
                          {/* Progress bar - always visible */}
                          <div className="w-12 h-1.5 bg-border rounded-full overflow-hidden">
                            <div
                              className={cn("h-full rounded-full transition-all", colorClass)}
                              style={{ width: `${Math.min(usagePercent, 100)}%` }}
                            />
                          </div>
                          {/* Desktop: show remaining tokens */}
                          <span className={cn("hidden sm:inline text-[10px] font-mono", textColorClass)}>
                            {formatTokens(remainingTokens)}
                          </span>
                        </button>
                      </DialogTrigger>
                      <DialogContent className="sm:max-w-[320px]">
                        <DialogHeader>
                          <DialogTitle>Context Usage</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 pt-2">
                          {/* Model & Thinking level */}
                          <div className="flex items-center gap-2">
                            <span className="px-2 py-1 rounded bg-primary/10 text-primary text-sm font-medium">
                              {MODEL_SHORT_NAMES[selectedChat.config!.model] || selectedChat.config!.model}
                            </span>
                            <span className="px-2 py-1 rounded bg-violet-500/10 text-violet-600 dark:text-violet-400 text-sm">
                              🧠 {THINKING_LEVELS.find(t => t.id === selectedChat.config!.thinkingLevel)?.name || selectedChat.config!.thinkingLevel}
                            </span>
                          </div>

                          {/* Progress bar */}
                          <div className="space-y-2">
                            <div className="flex justify-between text-sm">
                              <span className="text-muted-foreground">Used</span>
                              <span className={textColorClass}>{usagePercent.toFixed(1)}%</span>
                            </div>
                            <div className="w-full h-2 bg-border rounded-full overflow-hidden">
                              <div
                                className={cn("h-full rounded-full transition-all", colorClass)}
                                style={{ width: `${Math.min(usagePercent, 100)}%` }}
                              />
                            </div>
                          </div>

                          {/* Stats */}
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div className="space-y-1">
                              <p className="text-muted-foreground">Remaining</p>
                              <p className={cn("font-mono font-medium", textColorClass)}>
                                {formatTokens(remainingTokens)}
                              </p>
                            </div>
                            <div className="space-y-1">
                              <p className="text-muted-foreground">Total</p>
                              <p className="font-mono font-medium">
                                {formatTokens(contextWindow)}
                              </p>
                            </div>
                            <div className="space-y-1">
                              <p className="text-muted-foreground">Input</p>
                              <p className="font-mono">
                                {formatTokens(selectedChat.usage!.inputTokens)}
                              </p>
                            </div>
                            <div className="space-y-1">
                              <p className="text-muted-foreground">Output</p>
                              <p className="font-mono">
                                {formatTokens(selectedChat.usage!.outputTokens)}
                              </p>
                            </div>
                          </div>

                          {/* Cost */}
                          <div className="pt-2 border-t">
                            <div className="flex justify-between text-sm">
                              <span className="text-muted-foreground">Estimated cost</span>
                              <span className="font-mono text-amber-600 dark:text-amber-500">
                                {formatCost(selectedChat.usage!.totalCost)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                  );
                })()}

                {/* Token usage - desktop only, text format */}
                {selectedChat.usage && (selectedChat.usage.inputTokens > 0 || selectedChat.usage.outputTokens > 0) && (
                  <div className="hidden sm:flex items-center gap-1 text-[10px] sm:text-xs text-muted-foreground font-mono">
                    <span className="text-muted-foreground/50">|</span>
                    <span title={`Input: ${selectedChat.usage.inputTokens.toLocaleString()} | Output: ${selectedChat.usage.outputTokens.toLocaleString()}`}>
                      {formatTokens(selectedChat.usage.inputTokens + selectedChat.usage.outputTokens)}
                    </span>
                    <span className="text-muted-foreground/50">|</span>
                    <span className="text-amber-600 dark:text-amber-500" title="Estimated cost">
                      {formatCost(selectedChat.usage.totalCost)}
                    </span>
                  </div>
                )}

                {/* 3-dot menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => handleCopyId(selectedChat.id)}>
                      <Copy className="h-4 w-4 mr-2" />
                      Copy ID
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      variant="destructive"
                      onClick={() => setChatToDelete(selectedChat.id)}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {/* Connection Error Banner */}
            {connectionError && (
              <div className="p-3 bg-destructive/10 border-b border-destructive/20">
                <p className="text-sm text-destructive font-medium">
                  ❌ {connectionError.message}
                </p>
                {connectionError.details && (
                  <p className="text-xs text-destructive/80 mt-1 font-mono">
                    {connectionError.details}
                  </p>
                )}
              </div>
            )}

            {/* Messages wrapper */}
            <div className="flex-1 relative min-h-0">
              {/* Messages */}
              <div className="absolute inset-0 overflow-hidden">
                <ScrollArea
                  className="h-full w-full"
                  viewportRef={scrollRef}
                  onScroll={handleScroll}
                >
                {/* Centered loading spinner */}
                {isLoading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-background/80 z-10">
                    <div className="flex flex-col items-center gap-3">
                      <div className="relative w-10 h-10">
                        <div className="absolute inset-0 rounded-full border-4 border-muted"></div>
                        <div className="absolute inset-0 rounded-full border-4 border-primary border-t-transparent animate-spin"></div>
                      </div>
                      <span className="text-sm text-muted-foreground">Loading...</span>
                    </div>
                  </div>
                )}

                <div className="p-3 sm:p-4 space-y-4 max-w-[800px] mx-auto w-full min-w-0 overflow-hidden">
                  {(() => {
                    // Group tool_call with their corresponding tool_result
                    const renderedIndexes = new Set<number>()

                    // Find previous timestamp for duration calculation
                    const getPreviousTs = (currentIndex: number, currentType: string): number | undefined => {
                      // For assistant messages, find the previous user/tool_result message
                      if (currentType === 'assistant') {
                        for (let i = currentIndex - 1; i >= 0; i--) {
                          const prevEntry = history[i]
                          if (prevEntry.type === 'user' || prevEntry.type === 'tool_result') {
                            return prevEntry.ts
                          }
                        }
                      }
                      // For thinking entries, find the previous user/tool_result message as reference point
                      if (currentType === 'thinking') {
                        for (let i = currentIndex - 1; i >= 0; i--) {
                          const prevEntry = history[i]
                          if (prevEntry.type === 'user' || prevEntry.type === 'tool_result') {
                            return prevEntry.ts
                          }
                        }
                      }
                      return undefined
                    }

                    return history.map((entry, index) => {
                      // Skip already rendered entries (tool_results that were merged)
                      if (renderedIndexes.has(index)) {
                        return null
                      }

                      // For tool_call, find the matching tool_result
                      if (entry.type === 'tool_call' && entry.id) {
                        // Look ahead for matching tool_result
                        for (let i = index + 1; i < history.length; i++) {
                          const nextEntry = history[i]
                          if (nextEntry.type === 'tool_result' && nextEntry.id === entry.id) {
                            renderedIndexes.add(i)
                            return (
                              <ChatMessage
                                key={`${entry.ts}-${index}`}
                                entry={entry}
                                resultEntry={nextEntry}
                                onConfirmation={handleConfirmation}
                              />
                            )
                          }
                          // Skip meta_update and sub_agent_result (sub_agent_call renders separately)
                          if (nextEntry.type === 'sub_agent_result' || nextEntry.type === 'meta_update') {
                            continue
                          }
                          // Stop looking if we hit a non-tool entry
                          if (nextEntry.type !== 'tool_call' && nextEntry.type !== 'tool_result' && nextEntry.type !== 'sub_agent_call') {
                            break
                          }
                        }
                      }

                      // For sub_agent_call, find the matching sub_agent_result
                      if (entry.type === 'sub_agent_call') {
                        let subAgentResultEntry: typeof entry | undefined
                        // Look ahead for matching sub_agent_result by id (like tool_call/tool_result)
                        for (let i = index + 1; i < history.length; i++) {
                          const nextEntry = history[i]
                          if (nextEntry.type === 'sub_agent_result' && nextEntry.id === entry.id) {
                            renderedIndexes.add(i)
                            subAgentResultEntry = nextEntry
                            break
                          }
                        }
                        return (
                          <ChatMessage
                            key={`${entry.ts}-${index}`}
                            entry={entry}
                            subAgentResultEntry={subAgentResultEntry}
                            onConfirmation={handleConfirmation}
                          />
                        )
                      }

                      return (
                        <ChatMessage
                          key={`${entry.ts}-${index}`}
                          entry={entry}
                          previousTs={getPreviousTs(index, entry.type)}
                          isLast={index === history.length - 1}
                          onConfirmation={handleConfirmation}
                          isPendingConfirmation={entry.type === 'confirmation_request' && entry.confirmationId === pendingConfirmationId}
                        />
                      )
                    })
                  })()}

                  {/* Typing indicator when agent is processing */}
                  {isAgentProcessing && !isLoading && (
                    <div className="flex justify-start">
                      <div className="bg-muted rounded-2xl px-4 py-3">
                        <div className="flex gap-1">
                          <span className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                          <span className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                          <span className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </ScrollArea>
              </div>

              {/* Bottom gradient fade */}
              <div className="absolute bottom-0 left-0 right-2 h-8 bg-gradient-to-t from-card to-transparent pointer-events-none z-10" />

              {/* Scroll to bottom button */}
              {showScrollButton && (
                <Button
                  variant="secondary"
                  size="sm"
                  className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 shadow-lg rounded-full h-10 w-10 p-0"
                  onClick={() => scrollToBottom(true)}
                >
                  <ArrowDown className="h-5 w-5" />
                </Button>
              )}
            </div>

            {/* Input */}
            <div className="relative py-2 sm:py-4 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
              <div className="relative px-3 sm:px-4 max-w-[800px] mx-auto w-full">
                {/* Floating Stop button */}
                {selectedChat.status === 'running' && (
                  <Button
                    variant="secondary"
                    size="sm"
                    className="absolute right-3 sm:right-4 -top-2 -translate-y-full z-20 shadow-lg gap-1.5"
                    disabled={stoppingChatId === selectedChat.id}
                    onClick={() => handleStopChat(selectedChat.id)}
                  >
                    {stoppingChatId === selectedChat.id ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Square className="h-3.5 w-3.5" />
                    )}
                    Stop
                  </Button>
                )}
                <PromptInput
                  onSubmit={handleSubmit}
                  isLoading={isSending}
                >
                  <PromptInputTextarea
                    ref={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder={
                      isSending
                        ? 'Sending...'
                        : selectedChat.status === 'running'
                          ? 'Type a message...'
                          : 'Type to restart assistant...'
                    }
                    disabled={false}
                  />
                  <PromptInputSubmit disabled={!input.trim()} />
                </PromptInput>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Delete confirmation dialog */}
      <AlertDialog open={!!chatToDelete} onOpenChange={(open) => !open && setChatToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete chat?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The chat and all its messages will be permanently deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-white hover:bg-destructive/90"
              onClick={() => chatToDelete && handleDeleteChat(chatToDelete)}
            >
              {deletingChatId === chatToDelete ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
